import React from 'react';
import { Box } from '@heycater/design-system';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { getContentfulImageUrl } from 'hub/helpers/contentful';
import { Caterer } from 'lib/algolia/model';
import { parseContentfulCateringCategories } from 'lib/contentful/cateringCategoryMapping';
import { IDynamicLandingPageFields } from 'lib/contentful/landingPages';
import { ISearchModel } from 'lib/types/generated/contentful';
import { QualificationProvider } from 'qualification/context';
import useCity from 'qualification/hooks/usePreselectedCity';
import { findCitySlug } from 'shared/helpers/cities';
import { marketplaceURL } from 'shared/helpers/urls';
import { getLocalizedCity } from 'shared/hooks/useLocalizeCity';
import MainLayout from 'shared/layouts/MainLayout';
import {
  getRating,
  getRatingCount,
} from 'shared/services/trustedShops/handleRatings';
import { Ratings } from 'shared/services/trustedShops/model';
import CTAWithBenefits from 'static_pages/components/CTAWithBenefits';
import FooterLinks from 'static_pages/components/FooterLinks';
import ImageCardSlider, {
  ImageCardSliderItem,
} from 'static_pages/components/ImageCardSlider';
import CertificateSection from 'static_pages/marketing_pages/components/CertificateSection';
import Faqs from 'static_pages/marketing_pages/components/Faqs';
import TopCaterers from 'static_pages/marketing_pages/components/TopCaterers';
import BenefitsSection from 'static_pages/marketing_pages/dynamic_landing_page/components/BenefitsSection';
import Hero from 'static_pages/marketing_pages/dynamic_landing_page/components/Hero';
import HowItWorksCTA from 'static_pages/marketing_pages/dynamic_landing_page/components/HowItWorksCTA';
import PageSections from 'static_pages/marketing_pages/dynamic_landing_page/components/PageSections';
import RatingsSecondary from 'static_pages/marketing_pages/dynamic_landing_page/components/RatingsSecondary';
import ReadMoreArea from 'static_pages/marketing_pages/dynamic_landing_page/components/ReadMoreArea';
import RenderMarkdown from 'static_pages/marketing_pages/dynamic_landing_page/components/RenderMarkdown';
import StickySectionsNavigation from 'static_pages/marketing_pages/dynamic_landing_page/components/StickySectionsNavigation';
import {
  buildFaqMarkup,
  buildFunnelUrl,
  capitalizeFirstLetter,
} from 'static_pages/marketing_pages/helpers';
import DarkButton from 'system/components/Button/DarkButton';
import Container from 'system/components/Container';
import Typography from 'system/components/Typography';

type Page = IDynamicLandingPageFields & {
  bestRatedCaterers: Caterer[];
  mostBookedCaterers: Caterer[];
};

interface Props {
  page: Page;
  ratings: Ratings;
}

function DynamicLandingPage({ page, ratings }: Props) {
  const { t } = useTranslation('dynamic_landing_page');
  const router = useRouter();
  const { locale } = useRouter();
  const localizedCity = getLocalizedCity(page.city, t);
  const parsedCateringCategories = parseContentfulCateringCategories(
    page.keywords
  );

  const city = useCity({ cityFromPath: page.city });

  function mapCaterersToCards(items: Caterer[]): ImageCardSliderItem[] {
    return items.reduce<ImageCardSliderItem[]>((acc, caterer) => {
      if (caterer.profile_image) {
        acc.push({
          key: caterer.slogan_en || '',
          href: marketplaceURL(
            `/catering/${findCitySlug(page.city)}/${caterer.slug}`
          ),
          cta: capitalizeFirstLetter(
            locale === 'de' ? caterer.slogan_de : caterer.slogan_en
          ),
          ctaHighlight:
            t('common:preposition.from') + ' ' + caterer.company_name,
          imageSrc: caterer.profile_image,
          pillText: localizedCity,
        });
      }
      return acc;
    }, []);
  }

  function mapSearchModelsToCards(
    items: ISearchModel[]
  ): ImageCardSliderItem[] {
    return items.reduce<ImageCardSliderItem[]>((acc, { fields, sys }) => {
      const cta = fields.searchCta;
      const imageSrc = getContentfulImageUrl(fields.cardImage);

      if (cta && imageSrc) {
        acc.push({
          key: sys.id,
          href: buildFunnelUrl({
            city: fields.searchCity,
            cateringCategories:
              parseContentfulCateringCategories(
                fields.searchCateringCategory
              ) || parsedCateringCategories,
            router,
          }),
          cta: cta,
          imageSrc: imageSrc,
        });
      }
      return acc;
    }, []);
  }

  return (
    <MainLayout ratings={ratings} renderNavbarCta={() => null}>
      <QualificationProvider
        storage={null}
        initialized={{
          nonSkippableQuestions: ['catering_categories'],
          answers: {
            city,
            catering_categories: parsedCateringCategories,
          },
        }}
      >
        <Head>
          <title>{page.metaTitle}</title>
          <meta name="description" content={page.metaDescription} />
          <meta property="og:title" content={page.metaTitle} />
          <meta property="og:description" content={page.metaDescription} />

          {page.faQsEnabled && page?.faQs?.length && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(buildFaqMarkup(page.faQs)),
              }}
            />
          )}
        </Head>
        <PageSections.Provider>
          <Hero
            ratings={ratings}
            title={page.heroTitle}
            headerImage={page.headerImage}
          />
          <StickySectionsNavigation
            title={
              <Box pt={{ xs: 4, md: 8, lg: 10 }} pb={{ xs: 2, md: 1 }}>
                <NavigationSubtitleTypography align="center">
                  {t('topNavigationTitle')}
                </NavigationSubtitleTypography>
              </Box>
            }
          />
          <Box mt={{ xs: 0, md: 3 }} mb={{ xs: 0, md: 4 }}>
            <CertificateSection />
          </Box>
          <PageSections.TrackedSection
            id="benefits"
            label={t('section.benefits')}
          >
            <BenefitsSection />
          </PageSections.TrackedSection>
          {/* SECTION 1 */}
          {page.section1FilteredMPCard?.length ? (
            <PageSections.TrackedSection
              id="section-1"
              label={t('section.section1')}
              mt={2}
              pb={6}
            >
              <SectionTitle>{page.section1Header}</SectionTitle>
              <ImageCardSlider
                items={mapSearchModelsToCards(page.section1FilteredMPCard)}
              />

              <Box pt={6} pb={4} textAlign="center">
                <DarkCTAButton
                  href={buildFunnelUrl({
                    city: page.city,
                    cateringCategories: parsedCateringCategories,
                    router,
                  })}
                >
                  {page.section1Cta ? page.section1Cta : t('howItWorks.CTA')}
                </DarkCTAButton>
              </Box>

              <Container size="xsm">
                <ReadMoreArea>
                  <RenderMarkdown content={page.section1Body} />
                </ReadMoreArea>
              </Container>
            </PageSections.TrackedSection>
          ) : null}

          <HowItWorksCTA
            cta={page.buttonCta}
            cateringCategories={page.keywords}
            cityFromSlug={page.city}
          />

          {/* SECTION 2 */}
          <PageSections.TrackedSection
            id="top-caterers"
            isTracked={!!page.bestRatedCaterers?.length}
            label={t('section.topRatedCaterers')}
          >
            <TopCaterers
              cta={page.section2Cta}
              topCaterers={page.bestRatedCaterers}
              title={page.section2Header}
              city={page.city}
              cateringCategories={page.keywords}
            />

            <Container size="xsm">
              <ReadMoreArea>
                <RenderMarkdown content={page.section2Body} />
              </ReadMoreArea>
            </Container>
          </PageSections.TrackedSection>

          {/* SECTION 3 */}
          {page.mostBookedCaterers?.length ? (
            <PageSections.TrackedSection
              id="most-booked"
              isTracked={!!page.mostBookedCaterers?.length}
              label={t('section.mostPopularCaterers')}
            >
              <SectionTitle> {page.section3Cta || 'Top Caterers'}</SectionTitle>
              <ImageCardSlider
                items={mapCaterersToCards(page.mostBookedCaterers)}
              />
              <Container size="md">
                <Box pt={6} pb={4} textAlign="center">
                  <DarkCTAButton
                    href={buildFunnelUrl({
                      city: page?.city,
                      cateringCategories: parsedCateringCategories,
                      router,
                    })}
                  >
                    {page.section3Cta
                      ? page.section3Cta
                      : t('catererCards.cta')}
                  </DarkCTAButton>
                </Box>
              </Container>

              <Container size="xsm">
                <ReadMoreArea>
                  <RenderMarkdown content={page.section3Body} />
                </ReadMoreArea>
              </Container>
            </PageSections.TrackedSection>
          ) : null}

          {/* SECTION 4 */}
          {page.section4FilteredMpCard?.length ? (
            <PageSections.TrackedSection
              id="section-4"
              label={t('section.section4')}
              pb={{ xs: 4, md: 8 }}
            >
              <SectionTitle>{page.section4Header}</SectionTitle>
              <ImageCardSlider
                items={mapSearchModelsToCards(page.section4FilteredMpCard)}
              />
              <Container size="md">
                <Box pt={6} pb={4} textAlign="center">
                  <DarkCTAButton
                    href={buildFunnelUrl({
                      city: page.city,
                      cateringCategories: parsedCateringCategories,
                      router,
                    })}
                  >
                    {page.section4Cta ? page.section4Cta : t('howItWorks.CTA')}
                  </DarkCTAButton>
                </Box>
              </Container>
              <Container size="xsm">
                <ReadMoreArea>
                  <RenderMarkdown content={page.section4Body} />
                </ReadMoreArea>
              </Container>
            </PageSections.TrackedSection>
          ) : null}

          {page.faQsEnabled && page.faQs?.length && (
            <PageSections.TrackedSection id="faq" label="FAQ" pb={6}>
              <Faqs faqs={page.faQs} faqHeadline={page.faqSectionHeadline} />
            </PageSections.TrackedSection>
          )}
        </PageSections.Provider>
        <CTAWithBenefits cityFromSlug={page.city} containerSize="sm" />
        <FooterLinks pb={2} pt={4} />
        <RatingsSecondary
          rating={getRating(ratings)}
          ratingCount={getRatingCount(ratings)}
        />
      </QualificationProvider>
    </MainLayout>
  );
}

const NavigationSubtitleTypography = styled(Typography)`
  font-weight: 600;
  font-size: 16px;
  line-height: 150%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 18px;
  }
`;

const DarkCTAButton: React.FC<any> = ({ children, ...props }) => (
  <DarkButton size="lg" rounded as="a" target="_blank" {...props}>
    {children}
  </DarkButton>
);

const SectionTitleTypography = styled(Typography)`
  font-weight: 600;
  font-size: 24px;
  line-height: 150%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 36px;
  }
`;

const SectionTitle = styled(({ children }) => (
  <Container size="md">
    <Box py={6} textAlign="center">
      <SectionTitleTypography variant="h3" component="h2" gutterBottom>
        {children}
      </SectionTitleTypography>
    </Box>
  </Container>
))``;

export default DynamicLandingPage;
